import {
  IS_NESTED,
  OBJECT_TYPES,
} from 'src/tpbp-configuration/models/models.const'
import { getRecord } from 'src/services/deserializers/deserializers'
import { getRelations } from 'src/services/mappings/getMappings'
import { getContextualFields } from 'src/utils/fieldRules/fieldRules.utils'
import { filterFields } from 'src/utils/fields.utils'

const addNestedRelationships = (record, serialized) => {
  const relations = getRelations(record.$type)
  if (relations === undefined) {
    throw new Error(`relations for type "${record.$type}" not found`)
  }

  const nestedRelations = relations.filter(
    (relation) =>
      relation.isNested === IS_NESTED.yes ||
      (relation.isNested === IS_NESTED.ifNew && record.$isNew),
  )
  if (nestedRelations.length === 0) {
    return
  }

  const nestedData = nestedRelations.reduce((nestedData, relation) => {
    const data = record.$relationships[relation.originKey]
      ? record.$relationships[relation.originKey].data
      : null

    if (data) {
      if (relation.isMultiple) {
        nestedData[relation.originKey] = data.map((row) => {
          // TODO remove this double origin
          const type = row.$type || row.type
          const record = getRecord(type, row.id)

          if (!record) {
            return row
          }

          if (record.$type === OBJECT_TYPES.relation) {
            return {
              role: record.$attributes.role,
              related_to_id: record.$relationships.related_to.data.id,
            }
          }

          return record.$attributes
        })
      } else {
        const related = record.$relationships[relation.originKey]
        if (related?.data) {
          const key = relation.nestedKey || relation.originKey
          nestedData[key] = related.data.id
        }
      }
    }

    return nestedData
  }, {})

  Object.assign(serialized.attributes, nestedData)
}

const getNotNestedRelationships = (record) => {
  const relations = getRelations(record.$type)
  if (relations === undefined) {
    throw new Error(`relations for type "${record.$type}" not found`)
  }

  const notNestedRelations = relations.filter(
    (relation) => !relation.isNested && relation.isNested !== IS_NESTED.never,
  )

  if (notNestedRelations.length === 0) {
    // eslint-disable-next-line unicorn/no-useless-undefined
    return undefined
  }

  const notNestedRelationships = notNestedRelations.reduce(
    (notNestedRelationships, relation) => {
      const data = record.$relationships[relation.originKey]
      if (data) {
        notNestedRelationships[relation.originKey] = data
      }

      return notNestedRelationships
    },
    {},
  )

  return notNestedRelationships
}

const getContextualAttributes = (record) => {
  // TODO should be dynamic
  if (
    record.$type !== OBJECT_TYPES.portfolio &&
    record.$type !== OBJECT_TYPES.person
  ) {
    return record.$attributes
  }

  const fields = getContextualFields(record)
  const availableFields = filterFields(fields)

  const attributes = availableFields.reduce((attributes, field) => {
    attributes[field.originKey] = record.$attributes[field.originKey]

    return attributes
  }, {})

  return attributes
}

const serializer = (record) => {
  const relationships = getNotNestedRelationships(record)

  const attributes = getContextualAttributes(record)

  const serialized = {
    id: record.$isNew ? null : record.id,
    type: record.$type,
    attributes,
    relationships: relationships || undefined,
  }

  if (record.$isNew) {
    addNestedRelationships(record, serialized)
  }

  return serialized
}

const serialize2 = (record) => {
  if (record.$isNew) {
    return serializer(record)
  }

  const fields = getContextualFields(record)
  const availableFields = filterFields(fields)

  const attributes =
    record.$type === OBJECT_TYPES.ownership
      ? record.$attributes
      : record.$getDiff(availableFields)

  const patch = {
    id: record.id,
    type: record.$type,
    attributes,
  }
  addNestedRelationships(record, patch)

  const subscribedByIdIndex =
    record.$type === OBJECT_TYPES.portfolio
      ? availableFields.findIndex((field) => field.key === 'subscribedById')
      : -1

  let hasChanges = Object.keys(patch.attributes).length > 0

  if (subscribedByIdIndex >= 0) {
    const relationships = {
      subscribed_by: record.$relationships.subscribed_by,
    }
    Object.assign(patch, { relationships })
    hasChanges = true
  }

  if (!hasChanges) {
    // no change
    // eslint-disable-next-line consistent-return
    return
  }

  return patch
}

export { serializer, serialize2 }
